import { Button, Col, Form, Row } from "react-bootstrap";
import { useState } from "react";
import { Link } from "react-router-dom";

import refreshIcon from "assets/svg/refresh-green.svg";

import { HoverAuthorizeTooltip } from "components/authorize/AuthorizeTooltip";
import {
    checkActiveStatus,
    getGatewayType,
    isLoRaWanGateway,
} from "utils/gatewayFunctions";

import "assets/css/gateway.css";
import { ArrowIcon, Box } from "components/common";
import { GATEWAY_NAME_MESSAGE, Patterns } from "constant";
import { GatewayLDSUDetailsProps } from "types/Gateways";
import { isValidText } from "utils/functions";
import { GatewayType } from "constant/gateway";

const GatewayLDSUDetails = ({
    nameEditable,
    handleNameChange,
    isNameEditing,
    handleClickEdit,
    handleClickUpdate,
    isRefreshing,
    refreshThisGateway,
    timestampDetails,
    registryDetails,
    hasOnboard,
    gatewayId,
}: GatewayLDSUDetailsProps) => {
    const [lastRefreshTs, setLastRefreshTs] = useState<number>(
        Math.round(Date.now() / 1000)
    );

    // Must not use useMemo for this, because the result is also based on the current time
    // Ex: The heartbeat and info are the same when the gateway is offline
    const checkStatus =
        checkActiveStatus(
            timestampDetails.heartbeat,
            timestampDetails.info,
            lastRefreshTs
        ) || "-";

    return (
        <Row>
            <Col sm={12}>
                <div className="form-box mb-2">
                    <div className="gateway-detail-container">
                        <div className="gateway-detail-item top">
                            <Form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleClickUpdate();
                                }}
                            >
                                <Row>
                                    <Col xs="12">
                                        <Form.Label>Gateway Name</Form.Label>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs="8">
                                        <Form.Group className="mb-0">
                                            <Form.Control
                                                type="text"
                                                required
                                                aria-label="name"
                                                defaultValue={nameEditable}
                                                onChange={handleNameChange}
                                                disabled={!isNameEditing}
                                                isInvalid={
                                                    !isValidText(
                                                        "name",
                                                        {
                                                            name: nameEditable,
                                                        },
                                                        { name: "" },
                                                        Patterns.gatewayNamePattern
                                                    )
                                                }
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {GATEWAY_NAME_MESSAGE}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col xs="4">
                                        {!isNameEditing ? (
                                            <HoverAuthorizeTooltip
                                                className="w-100 mt-2 mt-md-0"
                                                permission="gateway:update"
                                            >
                                                <Button
                                                    type="button"
                                                    variant={"primary"}
                                                    className="w-100"
                                                    onClick={handleClickEdit}
                                                >
                                                    Edit
                                                </Button>
                                            </HoverAuthorizeTooltip>
                                        ) : (
                                            <Button
                                                type="button"
                                                variant={"primary"}
                                                className="w-100 mt-md-0"
                                                onClick={handleClickUpdate}
                                                disabled={
                                                    !isValidText(
                                                        "name",
                                                        {
                                                            name: nameEditable,
                                                        },
                                                        { name: "" },
                                                        Patterns.gatewayNamePattern
                                                    )
                                                }
                                            >
                                                Update
                                            </Button>
                                        )}
                                    </Col>
                                </Row>
                            </Form>
                        </div>
                        <div className="gateway-detail-bottom-container">
                            <div className="gateway-detail-item bottom">
                                <Row>
                                    <Col xs="12">
                                        <Form.Label>Status</Form.Label>
                                    </Col>
                                </Row>

                                <div className="d-flex align-items-center">
                                    <div className="ginfo">
                                        {isRefreshing
                                            ? "loading ..."
                                            : checkStatus}
                                    </div>
                                    <button
                                        type="button"
                                        className="p-0 border-0 bg-transparent"
                                        onClick={() => {
                                            const clickTs = Math.round(
                                                Date.now() / 1000
                                            );
                                            setLastRefreshTs(clickTs);
                                            refreshThisGateway();
                                        }}
                                    >
                                        <img
                                            className={
                                                isRefreshing
                                                    ? "btn refresh-animation"
                                                    : "btn"
                                            }
                                            src={refreshIcon}
                                            alt="refresh"
                                        />
                                    </button>
                                </div>
                            </div>
                            <div className="gateway-detail-item bottom">
                                <Row>
                                    <Col xs="12">
                                        <Form.Label>UUID</Form.Label>
                                    </Col>
                                </Row>
                                <div className="d-flex align-items-center h-38px">
                                    <h6 className="ginfo">
                                        {registryDetails.gateway_id}
                                    </h6>
                                </div>
                            </div>

                            {!isLoRaWanGateway(registryDetails) && (
                                <>
                                    <div className="gateway-detail-item bottom">
                                        <Row>
                                            <Col xs="12">
                                                <Form.Label>
                                                    {" "}
                                                    Firmware Version
                                                </Form.Label>
                                            </Col>
                                        </Row>
                                        <div className="d-flex align-items-center h-38px">
                                            <h6 className="ginfo">
                                                {registryDetails.info
                                                    ? `${registryDetails.info.FWV}`
                                                    : "-"}
                                            </h6>
                                        </div>
                                    </div>
                                    <div className="gateway-detail-item bottom">
                                        <Row>
                                            <Col xs="12">
                                                <Form.Label>
                                                    {" "}
                                                    Serial Number
                                                </Form.Label>
                                            </Col>
                                        </Row>
                                        <div className="d-flex align-items-center h-38px">
                                            <h6 className="ginfo">
                                                {registryDetails.serial}
                                            </h6>
                                        </div>
                                    </div>
                                    <div className="gateway-detail-item bottom last">
                                        {hasOnboard &&
                                            getGatewayType(
                                                registryDetails.info
                                            ) !== GatewayType.PCS && (
                                                <Box>
                                                    <Link
                                                        to={{
                                                            pathname: `/gateway-details/${gatewayId}/m2m`,
                                                            state: {
                                                                gatewayName:
                                                                    registryDetails.name,
                                                            },
                                                        }}
                                                        className="w-100"
                                                    >
                                                        Machine-to-Machine (M2M)
                                                        <ArrowIcon className="float-right" />
                                                    </Link>
                                                </Box>
                                            )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </Col>
        </Row>
    );
};

export default GatewayLDSUDetails;
